import { Card } from '@components/Card2';
import { CardEditContextProvider } from '@components/Card2/CardEditContextProvider';
import { CardPreviewContextProvider } from '@components/Card2/CardPreviewContextProvider/CardPreviewContextProvider';
import { CardFlavours } from '@components/Card2/types';
import { CelloReferralUpgradeDialog } from '@components/CelloReferralUpgradeDialog/CelloReferralUpgradeDialog';
import { LoggedInPremiumUpgradeDialog } from '@components/LoggedInPremiumUpgradeDialog/LoggedInPremiumUpgradeDialog';
import { Layout_WithUser } from '@components/WithUser/WithUser';
import { useCard } from '@hooks/card';
import { useTeamRole } from '@hooks/team-workspace';
import useNode from '@hooks/use-node';
import { blinqDatadogRum } from '@modules/monitoring/rum';
import { Box } from '@ui-lib';
import { useCardPageRouteInfo } from 'app-next/[workspace]/cards/hooks/use-card-page-route-info';
import { useRouter } from 'next/router';
import type { NextPageWithLayout } from 'pages/_app';

import { QRCodeSection } from '../../../components/QRCodeSection/QRCodeSection';
import PersonalCardPreviewLayout from './layout';
import styles from './page.module.scss';

const PersonalCardsPreviewPage: NextPageWithLayout = () => {
  const router = useRouter();

  const cardIdFromRoute = router.query.cardId as string;

  const { data, loading } = useCard(cardIdFromRoute);

  const headingNode = useNode<HTMLHeadingElement>();

  const { atLeastRole } = useTeamRole();
  const isTeamAdmin = atLeastRole('ADMIN');

  // This was used in the preset section to assist in scrolling to the field on card when editing starts.
  const [cardNode, cardRef] = useNode();

  const routeInfo = useCardPageRouteInfo(router);

  const recordTiming = (name: string) => {
    blinqDatadogRum.addTiming(name);
  };

  return (
    <>
      <Box
        display="flex"
        paddingBottom="large"
        marginLeft="xlarge"
        marginRight="large"
        marginTop="large"
      >
        <CardPreviewContextProvider hideEmptyAttributes={!isTeamAdmin}>
          <CardEditContextProvider flavour={CardFlavours.PERSONAL}>
            {routeInfo?.tab === 'card' && (
              <Box display="flex" width="full">
                <div className={styles['card-preset-container']}>
                  <Card
                    key={cardIdFromRoute}
                    headingNode={headingNode}
                    mode="clickable"
                    forwardedRef={cardRef}
                    loading={loading}
                    card={data?.card}
                    onBannerImageLoad={() => recordTiming('cover_image')}
                    onFloatingLeftImageLoad={() => recordTiming('logo_image')}
                    onFloatingRightImageLoad={() => recordTiming('profile_image')}
                  />
                </div>
              </Box>
            )}
            {routeInfo?.tab === 'qr-code' && data?.card && (
              <QRCodeSection
                parentCard={data.card}
                qrCodeImage={data?.card?.images?.embedded_qr_code_image}
                companyLogoImage={undefined}
              />
            )}
          </CardEditContextProvider>
        </CardPreviewContextProvider>
      </Box>
    </>
  );
};

// eslint-disable-next-line react/display-name

PersonalCardsPreviewPage.getLayout = (page: React.ReactElement) => {
  return (
    <Layout_WithUser>
      <PersonalCardPreviewLayout>{page}</PersonalCardPreviewLayout>
      <LoggedInPremiumUpgradeDialog />
      <CelloReferralUpgradeDialog />
    </Layout_WithUser>
  );
};

export default PersonalCardsPreviewPage;
